import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FV_NAVY = '#0D2137';
const FV_GOLD = '#C9A84C';
const FV_MUTED = '#5b6066';
const FV_BORDER = '#e8e4db';
const FV_SERIF = "'DM Serif Display', Georgia, serif";
const FV_SANS = "'DM Sans', 'Public Sans', -apple-system, sans-serif";

/**
 * FeaturedVideo — CMS-controlled "Video of the Week" widget.
 * Renders nothing (no reserved space) if the scope has no active video.
 * Click-to-load: poster + play button only on first paint; the iframe/video
 * element (and any third-party network request) is injected only after a click.
 */
export default function FeaturedVideo({ scope, style }) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setPlaying(false);
    axios.get(`${API}/videos/featured`, { params: { scope } })
      .then(r => setData(r.data))
      .catch(() => setData({ active: false }))
      .finally(() => setLoaded(true));
  }, [scope]);

  if (!loaded || !data || !data.active) return null;

  const { title, description, duration_label, cta_text, embed } = data;

  return (
    <div style={{ border: `1px solid ${FV_BORDER}`, borderRadius: 10, overflow: 'hidden', background: '#fff', ...style }} data-testid={`fv-${scope}`}>
      <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', background: FV_NAVY, overflow: 'hidden' }}>
        {!playing ? (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            style={{ position: 'absolute', inset: 0, border: 'none', padding: 0, margin: 0, cursor: 'pointer', width: '100%', height: '100%', background: 'none' }}
            data-testid={`fv-play-${scope}`}
            aria-label={`Play ${title}`}
          >
            {embed.poster && (
              <img src={embed.poster} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            )}
            <span style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(13,33,55,0.12), rgba(13,33,55,0.58))' }} aria-hidden="true" />
            <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }} aria-hidden="true">
              <span style={{ width: 58, height: 58, borderRadius: '50%', background: FV_GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 20px rgba(5,11,13,0.4)' }}>
                <svg width="19" height="19" viewBox="0 0 18 18" fill={FV_NAVY}><path d="M4 2.5v13l11-6.5z" /></svg>
              </span>
            </span>
            {duration_label && (
              <span style={{ position: 'absolute', left: 12, bottom: 12, background: 'rgba(5,11,13,0.85)', color: '#fff', fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6, fontFamily: FV_SANS }} data-testid={`fv-duration-${scope}`}>
                {duration_label}
              </span>
            )}
          </button>
        ) : embed.type === 'iframe' ? (
          <iframe
            src={embed.src}
            title={title}
            allow={embed.allow || 'autoplay; fullscreen'}
            allowFullScreen
            frameBorder="0"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
            data-testid={`fv-iframe-${scope}`}
          />
        ) : (
          <video
            src={embed.src}
            poster={embed.poster || undefined}
            controls
            autoPlay
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            data-testid={`fv-video-${scope}`}
          />
        )}
      </div>
      <div style={{ padding: '16px 18px 18px' }}>
        <div style={{ fontWeight: 700, fontSize: 10, letterSpacing: '1.3px', textTransform: 'uppercase', color: FV_GOLD, marginBottom: 8, fontFamily: FV_SANS }}>Video of the Week</div>
        <h3 style={{ fontFamily: FV_SERIF, fontWeight: 500, fontSize: 18, lineHeight: 1.2, margin: '0 0 8px', color: FV_NAVY }}>{title}</h3>
        {description && <p style={{ fontSize: 13, lineHeight: 1.5, color: FV_MUTED, margin: '0 0 12px' }}>{description}</p>}
        {!playing && (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            style={{ background: 'none', border: 'none', padding: 0, color: FV_NAVY, fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: FV_SANS }}
            data-testid={`fv-cta-${scope}`}
          >
            {cta_text || 'Watch Now'}&nbsp;→
          </button>
        )}
      </div>
    </div>
  );
}
